import { Assets, type Graphics, Rectangle, type Texture } from "pixi.js";
import { OutlineFilter } from "pixi-filters";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useMapObjectInteractions } from "../hooks/useMapObjectInteractions";
import { useScene } from "../hooks/useScene";
import { ACTIVE_INTERACTION_COLOR } from "../interaction";
import type { DockMapObject, LayerId, MapItemHandler, Point2D } from "../types";

const DOCK_ASSET_URL = new URL("./assets/Dock.png", import.meta.url).href;
const DOCK_SILHOUETTE_ASSET_URL = new URL(
    "./assets/Dock_silhouette.png",
    import.meta.url,
).href;
const DOCK_WORLD_WIDTH = 1.0;
const CALIBRATION_COLOR = 0x1900ff;
const RETURN_COLOR = 0x00ffff;
const HELPER_RING_RADIUS_WORLD = 0.14;
const HELPER_CENTER_RADIUS_WORLD = 0.05;
const HELPER_LINE_WIDTH_WORLD = 0.04;
const OUTLINE_THICKNESS = 3;

const OUTLINE_FILTER = new OutlineFilter({
    color: ACTIVE_INTERACTION_COLOR,
    thickness: OUTLINE_THICKNESS,
    knockout: true,
});

interface DockTextures {
    base: Texture;
    silhouette: Texture;
}

interface DockGeometry {
    center: Point2D;
    rotation: number;
    baseWidth: number;
    baseHeight: number;
    bounds: Rectangle;
}

let dockTexturesPromise: Promise<DockTextures> | undefined;

interface DockObjectProps {
    item: DockMapObject;
    layerId: LayerId;
    selected: boolean;
    onItemClick?: MapItemHandler<DockMapObject>;
    onItemHover?: MapItemHandler<DockMapObject>;
    onItemLeave?: MapItemHandler<DockMapObject>;
}

function getDockTextures(): Promise<DockTextures> {
    if (!dockTexturesPromise) {
        dockTexturesPromise = Promise.all([
            Assets.load<Texture>(DOCK_ASSET_URL),
            Assets.load<Texture>(DOCK_SILHOUETTE_ASSET_URL),
        ]).then(([base, silhouette]) => ({ base, silhouette }));
    }

    return dockTexturesPromise;
}

function useDockTextures(): DockTextures | null {
    const [textures, setTextures] = useState<DockTextures | null>(null);

    useEffect(() => {
        let mounted = true;

        void getDockTextures().then((loadedTextures) => {
            if (mounted) setTextures(loadedTextures);
        });

        return () => {
            mounted = false;
        };
    }, []);

    return textures;
}

function useDockGeometry(
    item: DockMapObject,
    textures: DockTextures | null,
): DockGeometry | null {
    const { coordinates } = useScene();

    return useMemo(() => {
        if (!textures) return null;

        const center = coordinates.worldToScene({ x: item.x, y: item.y });
        const baseWidth = coordinates.worldLengthToScene(DOCK_WORLD_WIDTH);
        const baseHeight =
            baseWidth * (textures.base.height / textures.base.width);
        const rotation = (90 - item.yaw) * (Math.PI / 180);
        const cos = Math.abs(Math.cos(rotation));
        const sin = Math.abs(Math.sin(rotation));
        const boundsWidth = baseWidth * cos + baseHeight * sin;
        const boundsHeight = baseWidth * sin + baseHeight * cos;
        const bounds = new Rectangle(
            center.x - boundsWidth / 2,
            center.y - boundsHeight / 2,
            boundsWidth,
            boundsHeight,
        );

        return { center, rotation, baseWidth, baseHeight, bounds };
    }, [coordinates, item.x, item.y, item.yaw, textures]);
}

function getOffsetPoint(
    item: DockMapObject,
    offset: DockMapObject["calibrationPointOffset"],
): Point2D {
    return {
        x: item.x + (offset?.x ?? 0),
        y: item.y + (offset?.y ?? 0),
    };
}

function pointsEqual(first: Point2D, second: Point2D): boolean {
    return first.x === second.x && first.y === second.y;
}

interface DockHelperPoint {
    position: Point2D;
    color: number;
}

interface DockHelperGeometry {
    dockPosition: Point2D;
    points: DockHelperPoint[];
    ringRadius: number;
    centerRadius: number;
    lineWidth: number;
    bounds: Rectangle;
}

function useDockHelperGeometry(item: DockMapObject): DockHelperGeometry {
    const { coordinates } = useScene();

    return useMemo(() => {
        const dockPosition = { x: item.x, y: item.y };
        const calibrationPoint = getOffsetPoint(
            item,
            item.calibrationPointOffset,
        );
        const returnPoint = getOffsetPoint(item, item.returnPointOffset);
        const dockScene = coordinates.worldToScene(dockPosition);
        const points: DockHelperPoint[] = [
            {
                position: coordinates.worldToScene(calibrationPoint),
                color: CALIBRATION_COLOR,
            },
        ];

        if (
            !pointsEqual(returnPoint, dockPosition) &&
            !pointsEqual(returnPoint, calibrationPoint)
        ) {
            points.push({
                position: coordinates.worldToScene(returnPoint),
                color: RETURN_COLOR,
            });
        }

        const ringRadius = coordinates.worldLengthToScene(
            HELPER_RING_RADIUS_WORLD,
        );
        const centerRadius = coordinates.worldLengthToScene(
            HELPER_CENTER_RADIUS_WORLD,
        );
        const lineWidth = coordinates.worldLengthToScene(
            HELPER_LINE_WIDTH_WORLD,
        );
        const boundPoints = [
            dockScene,
            ...points.map(({ position }) => position),
        ];
        const padding = ringRadius + lineWidth / 2;
        const xValues = boundPoints.map(({ x }) => x);
        const yValues = boundPoints.map(({ y }) => y);
        const minX = Math.min(...xValues);
        const maxX = Math.max(...xValues);
        const minY = Math.min(...yValues);
        const maxY = Math.max(...yValues);

        return {
            dockPosition: dockScene,
            points,
            ringRadius,
            centerRadius,
            lineWidth,
            bounds: new Rectangle(
                minX - padding,
                minY - padding,
                maxX - minX + padding * 2,
                maxY - minY + padding * 2,
            ),
        };
    }, [coordinates, item]);
}

function DockHelperGeometry({ item }: { item: DockMapObject }) {
    const geometry = useDockHelperGeometry(item);
    const drawHelpers = useCallback(
        (graphics: Graphics) => {
            graphics.clear();

            geometry.points.forEach(({ position, color }) => {
                graphics.setStrokeStyle({ color, width: geometry.lineWidth });
                graphics.moveTo(
                    geometry.dockPosition.x,
                    geometry.dockPosition.y,
                );
                graphics.lineTo(position.x, position.y);
                graphics.circle(position.x, position.y, geometry.ringRadius);
                graphics.stroke();
                graphics.setFillStyle({ color });
                graphics.circle(position.x, position.y, geometry.centerRadius);
                graphics.fill();
            });
        },
        [geometry],
    );

    return (
        <pixiContainer
            label="dock-helper-culling-unit"
            cullable
            cullableChildren={false}
            cullArea={geometry.bounds}
            eventMode="none"
        >
            <pixiGraphics eventMode="none" draw={drawHelpers} />
        </pixiContainer>
    );
}

const DockObject = ({
    item,
    layerId,
    selected,
    onItemClick,
    onItemHover,
    onItemLeave,
}: DockObjectProps) => {
    const textures = useDockTextures();
    const geometry = useDockGeometry(item, textures);
    const interaction = useMapObjectInteractions({
        item,
        layerId,
        onItemClick,
        onItemHover,
        onItemLeave,
    });

    const hitArea = useMemo(
        () =>
            textures
                ? new Rectangle(
                      -textures.base.width / 2,
                      -textures.base.height / 2,
                      textures.base.width,
                      textures.base.height,
                  )
                : null,
        [textures],
    );

    if (!textures || !geometry || !hitArea) return null;

    const active = interaction.hovered || selected;

    return (
        <>
            <pixiContainer
                label="dock-culling-unit"
                cullable
                cullableChildren={false}
                cullArea={geometry.bounds}
            >
                <pixiSprite
                    texture={textures.base}
                    x={geometry.center.x}
                    y={geometry.center.y}
                    width={geometry.baseWidth}
                    height={geometry.baseHeight}
                    rotation={geometry.rotation}
                    anchor={{ x: 0.5, y: 0.5 }}
                    eventMode={interaction.eventMode}
                    cursor={interaction.cursor}
                    hitArea={hitArea}
                    onPointerOver={interaction.onPointerOver}
                    onPointerOut={interaction.onPointerOut}
                    onPointerTap={interaction.onPointerTap}
                />
                {active && (
                    <pixiSprite
                        texture={textures.silhouette}
                        x={geometry.center.x}
                        y={geometry.center.y}
                        width={geometry.baseWidth}
                        height={geometry.baseHeight}
                        rotation={geometry.rotation}
                        anchor={{ x: 0.5, y: 0.5 }}
                        filters={[OUTLINE_FILTER]}
                        eventMode="none"
                    />
                )}
            </pixiContainer>
            {active && <DockHelperGeometry item={item} />}
        </>
    );
};

export default memo(DockObject, (prev, next) => {
    return (
        prev.item === next.item &&
        prev.layerId === next.layerId &&
        prev.selected === next.selected &&
        prev.onItemClick === next.onItemClick &&
        prev.onItemHover === next.onItemHover &&
        prev.onItemLeave === next.onItemLeave
    );
});
